<template>
    <div v-if="isConfirmationStepActive && user && membershipPlan">
        <card class="p-0 rounded-lg" id="confirm-membership-panel">
            <!-- Main Section Title -->
            <h4 class="section-title">
                <step-counter :current="currentStepNumber" :total="totalStepCount" />
                <span class="panel-title" v-if="membershipPlan.price_in_cents_with_vat > 0">
                    {{ trans('apps.online_courses.receipt_confirm') }}
                </span>
                <span class="panel-title" v-else>
                    {{ trans('apps.online_courses.subscribe_confirm') }}
                </span>
            </h4>

            <div
                id="collapseMembershipConfirm"
                role="tabpanel"
                aria-labelledby="headingTwo"
                data-parent="#event-accordion"
            >
                <form class="form-horizontal" role="form">
                    <div class="panel-body pb-0">
                        <div class="form-group m-b-10">
                            <label class="col-sm-3 control-label mb-0">{{ trans('apps.memberships.membership') }}</label>
                            <div class="col-sm-9">
                                <div class="form-control-static">{{ membershipPlan.name }}</div>
                            </div>
                        </div>

                        <div class="form-group m-b-10" v-if="membershipPlan.price_in_cents_with_vat">
                            <label class="col-sm-3 control-label mb-0">{{ trans('global.price') }}</label>
                            <div class="col-sm-9">
                                <div class="form-control-static" >
                                    {{ subscriptionPriceFormatted }} / {{ trans(`apps.memberships.frequencies.${membershipPlan.frequency}`) }}
                                    <br>
                                    <small>{{ renewalDescription }}</small>
                                </div>
                            </div>
                        </div>

                        <div class="form-group m-b-10" v-if="discount_code && discountMoneyUsed">
                            <label class="col-sm-3 control-label mb-0">{{ trans('global.discount') }}</label>
                            <div class="col-sm-9">
                                <div class="form-control-static">
                                    - {{ discountMoneyUsedFormatted }}
                                    ({{ discount_code.code }}
                                    {{ discount_code.type == 'percentage' ? discount_code.value + '%' : membershipPlan.currency + ' ' + discount_code.value }})
                                </div>
                            </div>
                        </div>

                        <div class="form-group m-b-10" v-if="discount_code && discountMoneyUsed">
                            <label class="col-sm-3 control-label mb-0" v-if="giftcardMoneyUsed">{{ trans('invoice.subtotal') }}</label>
                            <label class="col-sm-3 control-label mb-0" v-else>{{ trans('invoice.total') }}</label>
                            <div class="col-sm-9">
                                <div class="form-control-static">
                                    {{ membershipPriceWithDiscountFormatted }}
                                    <span class="text-success" v-if="totalMembershipPriceToPayNow > 0 && !giftcardMoneyUsed">
                                            ({{ trans('online_booking.to_pay_now') }}: {{ totalMembershipPriceToPayNowFormatted }})
                                        </span>
                                </div>
                            </div>
                        </div>

                        <div class="form-group m-b-10" v-if="giftcard && giftcardMoneyUsed">
                            <label class="col-sm-3 control-label mb-0">{{ trans('giftcards.title') }}</label>
                            <div class="col-sm-9">
                                <div class="form-control-static">
                                    - {{ giftcardMoneyUsedFormatted }} ({{ giftcard.name }})
                                </div>
                            </div>
                        </div>

                        <div class="form-group m-b-10" v-if="giftcard && giftcardMoneyUsed">
                            <label class="col-sm-3 control-label mb-0">{{ trans('invoice.total') }}</label>
                            <div class="col-sm-9">
                                <div class="form-control-static">
                                    {{ totalMembershipPriceToPayNowFormatted }}
                                    <span v-if="totalMembershipPriceToPayNow > 0" class="text-success">({{ trans('online_booking.to_pay_now') }}: {{ totalMembershipPriceToPayNowFormatted }})</span>
                                </div>
                            </div>
                        </div>

                        <client-address-info
                            v-if="info !== null"
                            :info="info"
                            :isClientLoggedIn="logged_in_client !== false"
                        ></client-address-info>

                        <div class="form-group m-b-10" v-if="info && info.notes">
                            <label class="col-sm-3 control-label mb-0">{{ trans('online_booking.fill_notes') }}</label>
                            <div class="col-sm-9">
                                <div class="form-control-static">{{ info.notes }}</div>
                            </div>
                        </div>

                        <div class="form-group m-b-15" :class="{ 'has-error' : errors.has('consent_accepted') }" v-if="is_consent_enabled && consent_settings.required_memberships && (!logged_in_client || (logged_in_client && (!logged_in_client.consent || logged_in_client.consent.is_consent_outdated)))">
                            <div class="col-sm-12 m-t-0">
                                <div class="checkbox">
                                    <input id="consent_accepted_event" type="checkbox" name="consent_accepted" v-model="consent_accepted" required>
                                    <label for="consent_accepted_event">
                                        {{ consent_settings.checkbox_label }}
                                    </label>
                                    <a href="" data-toggle="modal" data-target="#consentModal" >({{ trans('online_booking.read_more') }})</a>
                                </div>
                                <span v-show="errors.has('consent_accepted')" class="help-block m-b-0">{{ errors.first('consent_accepted') }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-between flex-wrap p-6 pt-0">
                        <previous-button  :previous-step-id="getPreviousStepId" />
                        <div v-if="totalMembershipPriceToPayNow > 0">
                            <ep-button
                                :loading="loading"
                                :disabled="loading"
                                @click="approve(1); loaderOn();"
                            >
                                <i class="zmdi zmdi-card mr-3"></i>
                                <span class="hidden sm:inline">{{ trans('vouchers.confirm_and_pay') }}</span>
                                <span class="sm:hidden">{{ trans('global.continue') }}</span>
                            </ep-button>
                        </div>
                        <div v-else>
                            <ep-button
                                :loading="loading"
                                :disabled="loading"
                                @click="approve(0); loaderOn();"
                            >
                                <span class="hidden sm:inline">{{ trans('apps.memberships.get_access_now') }}</span>
                                <span class="sm:hidden">{{ trans('global.continue') }}</span>
                                <i class="zmdi zmdi-arrow-right ml-3"></i>
                            </ep-button>
                        </div>
                    </div>
                </form>
            </div>
        </card>
    </div>

</template>

<script>
    import { http } from '@/system/http';
    import hasSelectedProducts from '@/online_booking/new/mixins/hasSelectedProducts.js';
    import ClientAddressInfo from '@/online_booking/new/components/common_booking/ClientAddressInfo.vue'
    import Card from '@/online_booking/new/components/common/Card';
    import StepCounter from '@/online_booking/new/components/common/StepCounter';
    import {mapGetters, mapActions, mapState} from 'vuex';
    import PreviousButton from '@/online_booking/new/components/common/PreviousButton';
    import EpButton from '@/system/components/global/button';

    export default {
        components: {
            Card,
            ClientAddressInfo,
            StepCounter,
            PreviousButton,
            EpButton,
        },

        data() {
            return {
                membershipPlan: null,
                user: null,
                info: null,
                payment: null,
                dataFrm: null,
                slug: null,
                logged_in_client: null,
                loading: false,
                is_consent_enabled: false,
                consent_settings: false,
                consent_accepted: false,
            }
        },

        props: {
            requestAccess: {
                default: false,
            },
        },

        mixins: [hasSelectedProducts],

        mounted() {
            this.slug = this.$route.params.slug;

            this.$event_bus.$on('confirmMembershipPlan', (data) => {
                if ($(window).width() < 500) {
                    setTimeout(function(){
                        $('html, body').animate({scrollTop:$('#buy-membership-plan-container').offset().top});
                    }, 250);
                };

                this.dataFrm = data.dataFrm;
                this.info = data.confirmInfo;

                this.$nextTick(function () {
                    this.setCurrentStep('confirmation');
                });
            });

            this.$event_bus.$on('LoadMembershipData', (data) => {
                this.membershipPlan = data.membership_plan;
                this.user = data.user;
                this.logged_in_client = data.logged_in_client;
                this.is_consent_enabled = data.is_consent_enabled;
                this.consent_settings = data.consent_settings;
            });

            $(document).on('show.bs.collapse', '#collapseMembershipConfirm', () => {
                this.$event_bus.$emit('ConfirmMembershipShowed');
            });
        },

        computed: {
            ...mapState('OnlineBooking', ['currentStep']),
            ...mapGetters('OnlineBooking', ['currentStepNumber', 'totalStepCount', 'getPreviousStepId']),

            subscriptionPriceFormatted() {
                return `${this.membershipPlan.currency} ${(this.membershipPlan.price_in_cents_with_vat / 100).toFixed(2)}`;
            },

            totalMembershipPriceToPayNow() {
                return Math.max(0, this.membershipPlan.price_in_cents_with_vat - this.discountMoneyUsed - this.giftcardMoneyUsed);
            },

            totalMembershipPriceToPayNowFormatted() {
                return this.membershipPlan.currency + ' ' + (this.totalMembershipPriceToPayNow / 100).toFixed(2);
            },

            giftcardMoneyUsed() {
                if (!this.giftcard || this.giftcard.money_left <= 0) {
                    return 0;
                }

                if (this.membershipPlan.price_in_cents_with_vat - this.discountMoneyUsed - this.giftcard.money_left >= 0) {
                    return this.giftcard.money_left;
                }

                if (this.membershipPlan.price_in_cents_with_vat - this.discountMoneyUsed - this.giftcard.money_left < 0) {
                    return this.membershipPlan.price_in_cents_with_vat - this.discountMoneyUsed;
                }

                return 0;
            },

            giftcardMoneyUsedFormatted() {
                return `${this.membershipPlan.currency} ${(this.giftcardMoneyUsed / 100).toFixed(2)}`;
            },

            discountMoneyUsed() {
                if (!this.discount_code) {
                    return 0;
                }

                if (this.discount_code.type == 'percentage') {
                    return Math.round(this.membershipPlan.price_in_cents_with_vat * (this.discount_code.value / 100));
                } else {
                    return Math.min(this.discount_code.value * 100, this.membershipPlan.price_in_cents_with_vat);
                }
            },

            discountMoneyUsedFormatted() {
                return `${this.membershipPlan.currency} ${(this.discountMoneyUsed / 100).toFixed(2)}`;
            },

            membershipPriceWithDiscountFormatted() {
                const price = Math.max(0, this.membershipPlan.price_in_cents_with_vat - this.discountMoneyUsed);

                return `${this.membershipPlan.currency} ${(price / 100).toFixed(2)}`;
            },

            isConfirmationStepActive () {
                return this.currentStep === 'confirmation';
            },

            renewalDescription() {
                const membershipIntervals = {
                    weekly: 'week',
                    monthly: 'month',
                    yearly: 'year'
                }

                const date = moment().add(1, membershipIntervals[this.membershipPlan.frequency])

                return trans('apps.memberships.renewal_description', {
                    price: this.totalMembershipPriceToPayNowFormatted,
                    interval: trans(`apps.memberships.renewal_intervals.${this.membershipPlan.frequency}`),
                    renewal_date: this.$filters.format_date(date, 'YYYY-MM-DD')
                })
            },
        },

        methods: {
            ...mapActions('OnlineBooking', ['setCurrentStep']),

            approve(pay_now) {

                this.dataFrm.append('pay_now', pay_now);

                if (this.consent_accepted) {
                    this.dataFrm.append('consent_accepted', this.consent_accepted);
                }

                if (this.giftcard) {
                    this.dataFrm.append('giftcard_id', this.giftcard.id);
                }

                if (this.discount_code) {
                    this.dataFrm.append('discount_code_id', this.discount_code.id);
                }

                http.post('/subscription/' + this.slug + '/choose-client', this.dataFrm)
                    .then(({data}) => {
                        if (data.payment){
                            this.$event_bus.$emit('membershipPayment', {data: data.data, dataFrm: this.dataFrm });
                        } else {
                            this.$event_bus.$emit('infoMembershipAdded', data.data);
                        }
                        this.loading = false;
                    })
                    .catch(({ errorBag }) => {
                        this.loading = false;
                        this.$setErrorBag(errorBag);
                    });
            },

            loaderOn() {
                this.loading = true;
            }
        },
    };


</script>
